<template>
  <div class="add-item-view">
    <!-- Header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Add item</h5>
    </div>

    <!-- Catalog picker + custom item form. Shared with the Create booking view,
         which stages the line instead of posting it. -->
    <item-picker
      :company-id="companyId"
      :company-group-id="companyGroupId"
      :game-id="gameId"
      :game-ids="gameIds"
      :game-name="gameName"
      :currency="currency"
      :currency-symbol="currencySymbol"
      :currency-format="currencyFormat"
      :number-format="numberFormat"
      :party-size="partySize"
      :saving="saving"
      :error-message="errorMessage"
      confirm-label="Add to order"
      @cancel="$emit('back')"
      @confirm="onConfirm"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import ItemPicker from '../components/ItemPicker.vue';

export default {
  name: 'AddItemView',

  components: { ItemPicker },

  props: {
    transactionId: { type: [String, Number], required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    // The experience(s) in context — items that are add-ons for any of them get
    // prioritised to the top of the grid and labelled. `gameId` is the single
    // booking case (booking drawer); `gameIds` covers a transaction that spans
    // several bookings (TransactionDetails).
    gameId: { type: [String, Number], default: null },
    gameIds: { type: Array, default: () => [] },
    gameName: { type: String, default: '' },
    currency: { type: String, default: 'USD' },
    currencySymbol: { type: String, default: '$' },
    currencyFormat: { type: Number, default: 0 },
    numberFormat: { type: Number, default: 0 },
    partySize: { type: Number, default: 0 }
  },

  data() {
    return {
      saving: false,
      errorMessage: ''
    };
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    async onConfirm(line) {
      const { line_total, ...payload } = line; // eslint-disable-line no-unused-vars
      this.saving = true;
      this.errorMessage = '';
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}` +
          `/transactions/${this.transactionId}/items`;
        await axios.post(url, payload, config);
        this.$emit('updated');
      } catch (error) {
        console.error('Failed to add item:', error);
        if (error.response?.status === 401) {
          this.$router.push('/forbidden');
          return;
        }
        this.errorMessage = error.response?.data?.message || error.response?.data || 'Failed to add item';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.add-item-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.view-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

</style>

<style lang="scss">
.white-content {
  .add-item-view {
    .view-header { border-bottom-color: #e9ecef; }
    .view-title { color: #344675; }
    .back-btn {
      background: #f0f2f5;
      border-color: #d1d5db;
      color: #525f7f;
      &:hover { background: #e2e6ea; color: #344675; }
    }
  }
}
</style>
